import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Helmet } from 'react-helmet';
import { LanguageContext } from '../../contexts/LanguageContext';
import { ThemeContext } from '../../contexts/ThemeContext';

const ManagedOpenShift = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      easing: 'ease-out-cubic'
    });
    
    return () => {
      AOS.refresh();
    };
  }, []);
  
  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 min-h-screen">
      <Helmet>
        <title>{translations['openshift.pageTitle']}</title>
        <meta name="description" content={translations['openshift.hero.description']} />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-800 dark:text-blue-50 mb-8" data-aos="fade-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">{translations['openshift.title'].split(' ')[0]}</span> {translations['openshift.title'].split(' ')[1]}
          </h1>
          
          <div className="bg-gradient-to-r from-indigo-600 via-blue-700 to-teal-600 text-white p-8 md:p-10 rounded-xl shadow-xl mb-12 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl" data-aos="zoom-in">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{translations['openshift.hero.title']}</h2>
            <p className="text-lg md:text-xl">
              {translations['openshift.hero.description']}
            </p>
          </div>
          
          <div className="mb-12" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <span className="inline-block mr-3 bg-blue-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
              {translations['openshift.power.title']}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
              {translations['openshift.power.description']}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-blue-600" data-aos="fade-right" data-aos-delay="100">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-blue-600 mr-2">⟡</span> {translations['openshift.enterprise.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['openshift.enterprise.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600" data-aos="fade-left" data-aos-delay="200">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-teal-600 mr-2">⟡</span> {translations['openshift.developer.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['openshift.developer.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-indigo-600" data-aos="fade-right" data-aos-delay="300">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-indigo-600 mr-2">⟡</span> {translations['openshift.multicloud.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['openshift.multicloud.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-yellow-600" data-aos="fade-left" data-aos-delay="400">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-yellow-600 mr-2">⟡</span> {translations['openshift.integration.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['openshift.integration.description']}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-16" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <span className="inline-block mr-3 bg-teal-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              </span>
              {translations['openshift.managed.title']}
            </h3>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              {translations['openshift.managed.description']}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="flex items-start" data-aos="fade-up" data-aos-delay="100">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['openshift.expert.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['openshift.expert.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-up" data-aos-delay="150">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-teal-600 to-teal-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['openshift.security.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['openshift.security.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-up" data-aos-delay="200">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-indigo-600 to-indigo-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['openshift.proactive.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['openshift.proactive.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-up" data-aos-delay="250">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-purple-600 to-purple-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['openshift.backup.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['openshift.backup.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-up" data-aos-delay="300">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-pink-600 to-pink-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['openshift.performance.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['openshift.performance.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-up" data-aos-delay="350">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-amber-600 to-amber-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['openshift.training.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['openshift.training.description']}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12" data-aos="fade-up">
            <div className="inline-block relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-teal-600 rounded-lg blur opacity-60 group-hover:opacity-100 transition duration-300"></div>
              <Link 
                to="/contact-us" 
                className="relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-bold text-lg rounded-lg shadow-xl hover:shadow-2xl transform transition duration-300 hover:-translate-y-1"
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                {translations['openshift.cta']}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagedOpenShift;
